import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';
import { tareasKeys } from '@/hooks/useTareas';
import type { ActividadTarea, AdjuntoTarea, Checklist, ComentarioTarea } from '@/types/tareas';

/* Checklists, comentarios, adjuntos e historial de una tarea (detalle rico, fase 2). */

export function useChecklists(tareaId: string) {
  return useQuery<Checklist[]>({
    queryKey: tareasKeys.checklists(tareaId),
    queryFn: () => api.get(`/api/tareas/${tareaId}/checklists`).then(r => r.data),
  });
}

export function useComentarios(tareaId: string) {
  return useQuery<ComentarioTarea[]>({
    queryKey: tareasKeys.comentarios(tareaId),
    queryFn: () => api.get(`/api/tareas/${tareaId}/comentarios`).then(r => r.data),
  });
}

export function useAdjuntos(tareaId: string) {
  return useQuery<AdjuntoTarea[]>({
    queryKey: tareasKeys.adjuntos(tareaId),
    queryFn: () => api.get(`/api/tareas/${tareaId}/adjuntos`).then(r => r.data),
  });
}

export function useActividad(tareaId: string, enabled: boolean) {
  return useQuery<ActividadTarea[]>({
    queryKey: tareasKeys.actividad(tareaId),
    queryFn: () => api.get(`/api/tareas/${tareaId}/actividad`).then(r => r.data),
    enabled,
  });
}

/**
 * Los endpoints de detalle devuelven la lista completa ya actualizada: se guarda directo en caché y
 * se refrescan los contadores de las tarjetas y el historial, sin recargar todo el detalle.
 */
function useAlGuardarLista<T>(clave: (id: string) => readonly unknown[], tareaId: string, tableroId?: string) {
  const qc = useQueryClient();
  return (lista: T) => {
    qc.setQueryData(clave(tareaId), lista);
    qc.invalidateQueries({ queryKey: tareasKeys.detalle(tareaId) });
    qc.invalidateQueries({ queryKey: tareasKeys.actividad(tareaId) });
    if (tableroId) qc.invalidateQueries({ queryKey: tareasKeys.tablero(tableroId) });
    qc.invalidateQueries({ queryKey: ['tareas', 'lista'] });
  };
}

export function useChecklistMutations(tareaId: string, tableroId?: string) {
  const onSuccess = useAlGuardarLista<Checklist[]>(tareasKeys.checklists, tareaId, tableroId);
  const onError = (e: unknown) => toast.error(getErrorMessage(e, 'No se pudo guardar el checklist'));

  return {
    crear: useMutation({
      mutationFn: (titulo: string) => api.post<Checklist[]>(`/api/tareas/${tareaId}/checklists`, { titulo }).then(r => r.data),
      onSuccess, onError,
    }),
    renombrar: useMutation({
      mutationFn: ({ id, titulo }: { id: string; titulo: string }) =>
        api.patch<Checklist[]>(`/api/tareas/checklists/${id}`, { titulo }).then(r => r.data),
      onSuccess, onError,
    }),
    eliminar: useMutation({
      mutationFn: (id: string) => api.delete<Checklist[]>(`/api/tareas/checklists/${id}`).then(r => r.data),
      onSuccess, onError,
    }),
    crearItem: useMutation({
      mutationFn: ({ checklistId, texto }: { checklistId: string; texto: string }) =>
        api.post<Checklist[]>(`/api/tareas/checklists/${checklistId}/items`, { texto }).then(r => r.data),
      onSuccess, onError,
    }),
    actualizarItem: useMutation({
      mutationFn: ({ checklistId, itemId, ...cambios }: { checklistId: string; itemId: string; texto?: string; completado?: boolean }) =>
        api.patch<Checklist[]>(`/api/tareas/checklists/${checklistId}/items/${itemId}`, cambios).then(r => r.data),
      onSuccess, onError,
    }),
    eliminarItem: useMutation({
      mutationFn: ({ checklistId, itemId }: { checklistId: string; itemId: string }) =>
        api.delete<Checklist[]>(`/api/tareas/checklists/${checklistId}/items/${itemId}`).then(r => r.data),
      onSuccess, onError,
    }),
  };
}

export function useComentarioMutations(tareaId: string, tableroId?: string) {
  const onSuccess = useAlGuardarLista<ComentarioTarea[]>(tareasKeys.comentarios, tareaId, tableroId);
  const onError = (e: unknown) => toast.error(getErrorMessage(e, 'No se pudo guardar el comentario'));

  return {
    crear: useMutation({
      mutationFn: ({ contenido, menciones }: { contenido: string; menciones: string[] }) =>
        api.post<ComentarioTarea[]>(`/api/tareas/${tareaId}/comentarios`, { contenido, menciones }).then(r => r.data),
      onSuccess, onError,
    }),
    editar: useMutation({
      mutationFn: ({ id, contenido }: { id: string; contenido: string }) =>
        api.patch<ComentarioTarea[]>(`/api/tareas/${tareaId}/comentarios/${id}`, { contenido }).then(r => r.data),
      onSuccess, onError,
    }),
    eliminar: useMutation({
      mutationFn: (id: string) =>
        api.delete<ComentarioTarea[]>(`/api/tareas/${tareaId}/comentarios/${id}`).then(r => r.data),
      onSuccess, onError,
    }),
  };
}

export function useAdjuntoMutations(tareaId: string, tableroId?: string) {
  const onSuccess = useAlGuardarLista<AdjuntoTarea[]>(tareasKeys.adjuntos, tareaId, tableroId);
  const onError = (e: unknown) => toast.error(getErrorMessage(e, 'No se pudo guardar el adjunto'));

  return {
    subir: useMutation({
      mutationFn: (archivo: File) => {
        const datos = new FormData();
        datos.append('file', archivo);
        return api.post<AdjuntoTarea[]>(`/api/tareas/${tareaId}/adjuntos`, datos, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }).then(r => r.data);
      },
      onSuccess, onError,
    }),
    eliminar: useMutation({
      mutationFn: (id: string) => api.delete<AdjuntoTarea[]>(`/api/tareas/${tareaId}/adjuntos/${id}`).then(r => r.data),
      onSuccess, onError,
    }),
  };
}

/** Pide una URL firmada (15 min) y abre el archivo en otra pestaña. */
export async function abrirAdjunto(adjuntoId: string): Promise<void> {
  try {
    const { data } = await api.get<{ url: string }>(`/api/tareas/adjuntos/${adjuntoId}/url`);
    window.open(data.url, '_blank', 'noopener,noreferrer');
  } catch (e) {
    toast.error(getErrorMessage(e, 'No se pudo abrir el archivo'));
  }
}
